import { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { tripsApi } from '../services/api';

export interface TripData {
  id: string;
  title: string;
  subtitle: string;
  durationDays: number;
  durationNights: number;
  location: string;
  description: string;
  overviewDescription?: string;
  image: string;
  itinerary: ItineraryDay[];
  faqs: FAQ[];
  active: boolean;
  mapLocation: {
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
  };
}

export interface ItineraryDay {
  day: number;
  title: string;
  description: string;
  location: string;
  destinations?: string[];
  activities?: string[];
  info?: string[];
  images?: string[];
  videos?: string[];
}

export interface FAQ {
  question: string;
  answer: string;
}

// Mapping from URL IDs to database IDs
export const urlToDatabaseIdMap: Record<string, string> = {
  'winter-trip': 'cmefxo59q0001n09a1dujud5p',
  'summer-trip': 'cmefxo59l0000n09als3vgwn1'
};

// Mapping from translated trip names to database names
export const tripNameToDatabaseNameMap: Record<string, string> = {
  'Viaggio Estivo': 'Summer Trip',
  'Summer Trip': 'Summer Trip',
  'Viaggio Invernale': 'Winter Trip',
  'Winter Trip': 'Winter Trip',
  // German
  'Sommerreise': 'Summer Trip',
  'Winterreise': 'Winter Trip',
  'Winter Reise': 'Winter Trip',
  // Spanish (common variants)
  'Viaje de Verano': 'Summer Trip',
  'Viaje de verano': 'Summer Trip',
  'Viaje Estival': 'Summer Trip',
  'Viaje de Invierno': 'Winter Trip',
  'Viaje Invernal': 'Winter Trip',
  // French
  'Voyage Estival': 'Summer Trip',
  'Voyage d\'Été': 'Summer Trip',
  'Voyage d\'ete': 'Summer Trip',
  'Voyage Hivernal': 'Winter Trip',
  // Dutch
  'Zomerreis': 'Summer Trip',
  'Zomer Reis': 'Summer Trip',
  'Winterreis': 'Winter Trip',
  // Turkish
  'Yaz Gezisi': 'Summer Trip',
  'Kış Gezisi': 'Winter Trip'
};

// Function to get database ID from URL ID
export const getDatabaseId = (urlId: string): string => {
  return urlToDatabaseIdMap[urlId] || urlId;
};

// Function to get database name from translated name
export const getDatabaseName = (translatedName: string): string => {
  return tripNameToDatabaseNameMap[translatedName] || translatedName;
};

// Hook to get translated trip data
export const useTripData = (tripId: string): TripData | null => {
  const { t } = useLanguage();
  
  const tripsData: Record<string, TripData> = {
    'winter-trip': {
      id: 'cmefxo59q0001n09a1dujud5p',
      title: t('trips.winterTrip.title'),
      subtitle: t('trips.winterTrip.subtitle'),
      durationDays: 7,
      durationNights: 6,
      location: t('trips.winterTrip.location'),
      description: t('trips.winterTrip.description'),
      image: '/trips/winter-trip.webp',
      itinerary: [],
      active: false,
      faqs: [
        {
          question: t('trips.winterTrip.faqs.dogs.question'),
          answer: t('trips.winterTrip.faqs.dogs.answer')
        },
        {
          question: t('trips.winterTrip.faqs.packing.question'),
          answer: t('trips.winterTrip.faqs.packing.answer')
        },
        {
          question: t('trips.winterTrip.faqs.transportation.question'),
          answer: t('trips.winterTrip.faqs.transportation.answer')
        }
      ],
      mapLocation: {
        name: 'Gran Sasso d\'Italia',
        address: '64047 Pietracamela, Province of Teramo, Italy',
        coordinates: { lat: 42.4667, lng: 13.5667 }
      }
    },
    'summer-trip': {
      id: 'cmefxo59l0000n09als3vgwn1',
      title: t('trips.summerTrip.title'),
      subtitle: t('trips.summerTrip.subtitle'),
      durationDays: 7,
      durationNights: 6,
      location: t('trips.summerTrip.location'),
      description: t('trips.summerTrip.description'),
      overviewDescription: t('trips.summerTrip.overviewDescription'),
      image: '/trips/summer-trip.webp',
      active: true,
      itinerary: [
        {
          day: 1,
          title: t('trips.summerTrip.itinerary.titles.airportPickup'),
          description: t('trips.summerTrip.itinerary.descriptions.airportPickup'),
          location: 'Aeroporto',
          info: ['pick-up at airport'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 1,
          title: t('trips.summerTrip.itinerary.destinations.collaltoSabino'),
          description: t('trips.summerTrip.itinerary.descriptions.collaltoSabino'),
          location: 'Collalto Sabino, Italia',
          destinations: ['collalto_sabino'],
          images: [
            '/destinations/collalto_sabino/collalto_sabino_1_medium.jpg',
            '/destinations/collalto_sabino/collalto_sabino_2_medium.jpg',
            '/destinations/collalto_sabino/collalto_sabino_3_medium.jpg'
          ]
        },
        {
          day: 1,
          title: t('trips.summerTrip.itinerary.destinations.santoStefano'),
          description: t('trips.summerTrip.itinerary.descriptions.santoStefano'),
          location: 'Santo Stefano di Sessanio, Italia',
          destinations: ['santo_stefano_di_sessanio'],
          images: [
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_1_medium.jpg',
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_2_medium.jpg',
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_3_medium.jpg'
          ]
        },
        {
          day: 1,
          title: t('trips.summerTrip.itinerary.titles.sextantioHotel'),
          description: t('trips.summerTrip.itinerary.descriptions.sextantioHotel'),
          location: 'Santo Stefano di Sessanio, Italia',
          activities: ['sextantio']
        },
        {
          day: 2,
          title: t('trips.summerTrip.itinerary.destinations.roccaCalascio'),
          description: t('trips.summerTrip.itinerary.descriptions.roccaCalascio'),
          location: 'Rocca Calascio, Italia',
          destinations: ['rocca_calascio'],
          images: [
            '/destinations/rocca_calascio/rocca_calascio_1_medium.jpg',
            '/destinations/rocca_calascio/rocca_calascio_2_medium.jpg',
            '/destinations/rocca_calascio/rocca_calascio_3_medium.jpg'
          ]
        },
        {
          day: 2,
          title: t('trips.summerTrip.itinerary.titles.arrosticini'),
          description: t('trips.summerTrip.itinerary.descriptions.arrosticini'),
          location: 'Mucciante, Italia',
          activities: ['arrosticini_mucciante'],
          images: [
            '/activities/arrosticini_mucciante/arrosticini_mucciante_1.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_2.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_3.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_4.jpg'
          ]
        },
        {
          day: 2,
          title: t('trips.summerTrip.itinerary.destinations.campoImperatore'),
          description: t('trips.summerTrip.itinerary.descriptions.campoImperatore'),
          location: 'Campo Imperatore, Italia',
          destinations: ['campo_imperatore'],
          images: [
            '/destinations/gran_sasso/gran_sasso_1_medium.jpg',
            '/destinations/gran_sasso/gran_sasso_2_medium.jpg',
            '/destinations/gran_sasso/gran_sasso_3_medium.jpg'
          ]
        },
        {
          day: 2,
          title: t('trips.summerTrip.itinerary.titles.overnightXXX'),
          description: t('trips.summerTrip.itinerary.descriptions.overnightXXX'),
          location: 'Campo Imperatore, Italia',
          info: ['pernottamento a XXX'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 3,
          title: t('trips.summerTrip.itinerary.titles.deerSpotting'),
          description: t('trips.summerTrip.itinerary.descriptions.deerSpotting'),
          location: 'Parco Nazionale d\'Abruzzo, Italia',
          activities: ['deer_spotting'],
          images: [
            '/activities/deer_spotting/deer_spotting_1.jpg',
            '/activities/deer_spotting/deer_spotting_2.jpg',
            '/activities/deer_spotting/deer_spotting_3.jpg',
            '/activities/deer_spotting/deer_spotting_4.jpg',
            '/activities/deer_spotting/deer_spotting_5.jpg',
            '/activities/deer_spotting/deer_spotting_6.jpg',
            '/activities/deer_spotting/deer_spotting_7.jpg'
          ]
        },
        {
          day: 3,
          title: t('trips.summerTrip.itinerary.destinations.scanno'),
          description: t('trips.summerTrip.itinerary.descriptions.scanno'),
          location: 'Scanno, Italia',
          destinations: ['scanno'],
          images: [
            '/destinations/scanno/scanno_1.jpg',
            '/destinations/scanno/scanno_2.jpg',
            '/destinations/scanno/scanno_3.jpg',
            '/destinations/scanno/scanno_4.jpg',
            '/destinations/scanno/scanno_5.jpg',
            '/destinations/scanno/scanno_6.jpg',
            '/destinations/scanno/scanno_7.jpg',
            '/destinations/scanno/scanno_8.jpg',
            '/destinations/scanno/scanno_9.jpg',
            '/destinations/scanno/scanno_10.jpg',
            '/destinations/scanno/scanno_11.jpg',
            '/destinations/scanno/scanno_12.jpg',
            '/destinations/scanno/scanno_13.jpg',
            '/destinations/scanno/scanno_14.jpg',
            '/destinations/scanno/scanno_15.jpg',
            '/destinations/scanno/scanno_16.jpg',
            '/destinations/scanno/scanno_17.jpg',
            '/destinations/scanno/scanno_18.jpg',
            '/destinations/scanno/scanno_19.jpg'
          ]
        },
        {
          day: 3,
          title: t('trips.summerTrip.itinerary.titles.wineTasting'),
          description: t('trips.summerTrip.itinerary.descriptions.wineTasting'),
          location: 'Azienda Tilli, Italia',
          activities: ['cantina_tilli'],
          images: [
            '/activities/cantina_tilli/cantina_tilli_1_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_2_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_3_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_4_medium.webp'
          ]
        },
        {
          day: 3,
          title: t('trips.summerTrip.itinerary.titles.campingOvernight'),
          description: t('trips.summerTrip.itinerary.descriptions.campingOvernight'),
          location: 'Camping, Italia',
          info: ['pernottamento al camping'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 4,
          title: t('trips.summerTrip.itinerary.titles.trabocchiCoast'),
          description: t('trips.summerTrip.itinerary.descriptions.trabocchiCoast'),
          location: 'Costa dei Trabocchi, Italia',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 5,
          title: t('trips.summerTrip.itinerary.titles.trabocchiCoast'),
          description: t('trips.summerTrip.itinerary.descriptions.trabocchiCoast'),
          location: 'Costa dei Trabocchi, Italia',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 6,
          title: t('trips.summerTrip.itinerary.titles.trabocchiCoast'),
          description: t('trips.summerTrip.itinerary.descriptions.trabocchiCoast'),
          location: 'Costa dei Trabocchi, Italia',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 7,
          title: t('trips.summerTrip.itinerary.titles.lastMoments'),
          description: t('trips.summerTrip.itinerary.descriptions.lastMoments'),
          location: 'Costa dei Trabocchi, Italia',
          info: ['continua a gustarti gli ultimi momenti della vacanza'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 7,
          title: t('trips.summerTrip.itinerary.titles.airportReturn'),
          description: t('trips.summerTrip.itinerary.descriptions.airportReturn'),
          location: 'Aeroporto',
          info: ['ritorno all\'aeroporto'],
          images: [
            '/trips/summer-trip.webp'
          ]
        }
      ],
      faqs: [
        {
          question: t('trips.summerTrip.faqs.when.question'),
          answer: t('trips.summerTrip.faqs.when.answer')
        },
        {
          question: t('trips.summerTrip.faqs.ageLimit.question'),
          answer: t('trips.summerTrip.faqs.ageLimit.answer')
        },
        {
          question: t('trips.summerTrip.faqs.soloTravel.question'),
          answer: t('trips.summerTrip.faqs.soloTravel.answer')
        },
        {
          question: t('trips.summerTrip.faqs.groupSize.question'),
          answer: t('trips.summerTrip.faqs.groupSize.answer')
        },
        {
          question: t('trips.summerTrip.faqs.minimumParticipants.question'),
          answer: t('trips.summerTrip.faqs.minimumParticipants.answer')
        },
        {
          question: t('trips.summerTrip.faqs.guideLanguages.question'),
          answer: t('trips.summerTrip.faqs.guideLanguages.answer')
        },
        {
          question: t('trips.summerTrip.faqs.whatToBring.question'),
          answer: t('trips.summerTrip.faqs.whatToBring.answer')
        },
        {
          question: t('trips.summerTrip.faqs.pets.question'),
          answer: t('trips.summerTrip.faqs.pets.answer')
        },
        {
          question: t('trips.summerTrip.faqs.flightIncluded.question'),
          answer: t('trips.summerTrip.faqs.flightIncluded.answer')
        },
        {
          question: t('trips.summerTrip.faqs.airportPickup.question'),
          answer: t('trips.summerTrip.faqs.airportPickup.answer')
        },
        {
          question: t('trips.summerTrip.faqs.arrivalTime.question'),
          answer: t('trips.summerTrip.faqs.arrivalTime.answer')
        },
        {
          question: t('trips.summerTrip.faqs.departureTime.question'),
          answer: t('trips.summerTrip.faqs.departureTime.answer')
        },
        {
          question: t('trips.summerTrip.faqs.internalTransfers.question'),
          answer: t('trips.summerTrip.faqs.internalTransfers.answer')
        },
        {
          question: t('trips.summerTrip.faqs.earlyLateArrival.question'),
          answer: t('trips.summerTrip.faqs.earlyLateArrival.answer')
        },
        {
          question: t('trips.summerTrip.faqs.differentDates.question'),
          answer: t('trips.summerTrip.faqs.differentDates.answer')
        },
        {
          question: t('trips.summerTrip.faqs.otherAirports.question'),
          answer: t('trips.summerTrip.faqs.otherAirports.answer')
        },
        {
          question: t('trips.summerTrip.faqs.recognizeGuide.question'),
          answer: t('trips.summerTrip.faqs.recognizeGuide.answer')
        },
        {
          question: t('trips.summerTrip.faqs.accommodation.question'),
          answer: t('trips.summerTrip.faqs.accommodation.answer')
        },
        {
          question: t('trips.summerTrip.faqs.meals.question'),
          answer: t('trips.summerTrip.faqs.meals.answer')
        },
        {
          question: t('trips.summerTrip.faqs.wifiAirConditioning.question'),
          answer: t('trips.summerTrip.faqs.wifiAirConditioning.answer')
        },
        {
          question: t('trips.summerTrip.faqs.sextantioRooms.question'),
          answer: t('trips.summerTrip.faqs.sextantioRooms.answer')
        },
        {
          question: t('trips.summerTrip.faqs.campingRooms.question'),
          answer: t('trips.summerTrip.faqs.campingRooms.answer')
        },
        {
          question: t('trips.summerTrip.faqs.dietaryOptions.question'),
          answer: t('trips.summerTrip.faqs.dietaryOptions.answer')
        },
        {
          question: t('trips.summerTrip.faqs.foodIntolerances.question'),
          answer: t('trips.summerTrip.faqs.foodIntolerances.answer')
        },
        {
          question: t('trips.summerTrip.faqs.accommodationNames.question'),
          answer: t('trips.summerTrip.faqs.accommodationNames.answer')
        },
        {
          question: t('trips.summerTrip.faqs.twinBeds.question'),
          answer: t('trips.summerTrip.faqs.twinBeds.answer')
        },
        {
          question: t('trips.summerTrip.faqs.fitnessLevel.question'),
          answer: t('trips.summerTrip.faqs.fitnessLevel.answer')
        },
        {
          question: t('trips.summerTrip.faqs.bikesIncluded.question'),
          answer: t('trips.summerTrip.faqs.bikesIncluded.answer')
        },
        {
          question: t('trips.summerTrip.faqs.bikeOptions.question'),
          answer: t('trips.summerTrip.faqs.bikeOptions.answer')
        },
        {
          question: t('trips.summerTrip.faqs.bikeExperience.question'),
          answer: t('trips.summerTrip.faqs.bikeExperience.answer')
        },
        {
          question: t('trips.summerTrip.faqs.optionalActivities.question'),
          answer: t('trips.summerTrip.faqs.optionalActivities.answer')
        },
        {
          question: t('trips.summerTrip.faqs.wolfSpotting.question'),
          answer: t('trips.summerTrip.faqs.wolfSpotting.answer')
        },
        {
          question: t('trips.summerTrip.faqs.foodTastings.question'),
          answer: t('trips.summerTrip.faqs.foodTastings.answer')
        },
        {
          question: t('trips.summerTrip.faqs.buyProducts.question'),
          answer: t('trips.summerTrip.faqs.buyProducts.answer')
        },
        {
          question: t('trips.summerTrip.faqs.badWeather.question'),
          answer: t('trips.summerTrip.faqs.badWeather.answer')
        },
        {
          question: t('trips.summerTrip.faqs.freeTime.question'),
          answer: t('trips.summerTrip.faqs.freeTime.answer')
        },
        {
          question: t('trips.summerTrip.faqs.swimming.question'),
          answer: t('trips.summerTrip.faqs.swimming.answer')
        },
        {
          question: t('trips.summerTrip.faqs.skipActivity.question'),
          answer: t('trips.summerTrip.faqs.skipActivity.answer')
        },
        {
          question: t('trips.summerTrip.faqs.howToBook.question'),
          answer: t('trips.summerTrip.faqs.howToBook.answer')
        },
        {
          question: t('trips.summerTrip.faqs.paymentSecurity.question'),
          answer: t('trips.summerTrip.faqs.paymentSecurity.answer')
        },
        {
          question: t('trips.summerTrip.faqs.cancellationPolicy.question'),
          answer: t('trips.summerTrip.faqs.cancellationPolicy.answer')
        },
        {
          question: t('trips.summerTrip.faqs.travelDocuments.question'),
          answer: t('trips.summerTrip.faqs.travelDocuments.answer')
        },
        {
          question: t('trips.summerTrip.faqs.travelInsurance.question'),
          answer: t('trips.summerTrip.faqs.travelInsurance.answer')
        },
        {
          question: t('trips.summerTrip.faqs.tripCancellation.question'),
          answer: t('trips.summerTrip.faqs.tripCancellation.answer')
        },
        {
          question: t('trips.summerTrip.faqs.changeParticipant.question'),
          answer: t('trips.summerTrip.faqs.changeParticipant.answer')
        },
        {
          question: t('trips.summerTrip.faqs.giftBooking.question'),
          answer: t('trips.summerTrip.faqs.giftBooking.answer')
        },
        {
          question: t('trips.summerTrip.faqs.guidePresence.question'),
          answer: t('trips.summerTrip.faqs.guidePresence.answer')
        },
        {
          question: t('trips.summerTrip.faqs.contactBeforeTrip.question'),
          answer: t('trips.summerTrip.faqs.contactBeforeTrip.answer')
        },
        {
          question: t('trips.summerTrip.faqs.whatsappContact.question'),
          answer: t('trips.summerTrip.faqs.whatsappContact.answer')
        },
        {
          question: t('trips.summerTrip.faqs.whatsappGroup.question'),
          answer: t('trips.summerTrip.faqs.whatsappGroup.answer')
        },
        {
          question: t('trips.summerTrip.faqs.localContact.question'),
          answer: t('trips.summerTrip.faqs.localContact.answer')
        },
        {
          question: t('trips.summerTrip.faqs.emergency.question'),
          answer: t('trips.summerTrip.faqs.emergency.answer')
        },
        {
          question: t('trips.summerTrip.faqs.flightBooking.question'),
          answer: t('trips.summerTrip.faqs.flightBooking.answer')
        },
        {
          question: t('trips.summerTrip.faqs.restaurantRecommendations.question'),
          answer: t('trips.summerTrip.faqs.restaurantRecommendations.answer')
        },
        {
          question: t('trips.summerTrip.faqs.ecoFriendly.question'),
          answer: t('trips.summerTrip.faqs.ecoFriendly.answer')
        },
        {
          question: t('trips.summerTrip.faqs.localEconomy.question'),
          answer: t('trips.summerTrip.faqs.localEconomy.answer')
        }
      ],
      mapLocation: {
        name: 'Rocca Calascio',
        address: '67020 Rocca Calascio, Province of L\'Aquila, Italy',
        coordinates: { lat: 42.3283, lng: 13.6889 }
      }
    }
  };

  return tripsData[tripId] || null;
};

// Legacy function for backward compatibility (returns English by default)
export const getTripData = (tripId: string): TripData | null => {
  const tripsData: Record<string, TripData> = {
    'winter-trip': {
      id: 'winter-trip',
      title: 'Winter Trip',
      subtitle: 'In the magic snow',
      durationDays: 7,
      durationNights: 6,
      location: 'Multiple locations in Abruzzo',
      description: 'Abruzzo in the snow is magic.',
      image: '/trips/winter-trip.webp',
      itinerary: [],
      active: false,
      faqs: [
        {
          question: 'Can I bring dogs',
          answer: 'Yes, dogs are welcome on most of our trips. Please contact us for specific details.'
        },
        {
          question: 'What should I pack?',
          answer: 'Warm clothing, hiking boots, and a sense of adventure are essential for this winter trip.'
        },
        {
          question: 'Is transportation included?',
          answer: 'Yes, all transportation between destinations is included in the trip price.'
        }
      ],
      mapLocation: {
        name: 'Gran Sasso d\'Italia',
        address: '64047 Pietracamela, Province of Teramo, Italy',
        coordinates: { lat: 42.4667, lng: 13.5667 }
      }
    },
    'summer-trip': {
      id: 'summer-trip',
      title: 'Summer Trip',
      subtitle: 'Explore the beauty of Abruzzo with the sun',
      durationDays: 7,
      durationNights: 6,
      location: 'Multiple locations in Abruzzo',
      description: 'Discover the natural beauty of Abruzzo in summer.',
      overviewDescription: 'Imagine embarking on a journey where time seems to flow more slowly, where every step is an encounter with history and every glance opens onto breathtaking landscapes. Our organized tour of Abruzzo of 7 days and 6 nights will lead you to discover an authentic land, between majestic mountains, timeless villages and the poetry of the sea of the Trabocchi Coast.',
      image: '/trips/summer-trip.webp',
      active: true,
      itinerary: [
        {
          day: 1,
          title: 'Pick-up at airport',
          description: 'Welcome to Abruzzo! Our team will welcome you at the airport to start this fantastic adventure.',
          location: 'Airport',
          info: ['pick-up at airport'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 1,
          title: 'Collalto Sabino',
          description: 'Discover the medieval charm of Collalto Sabino, a village rich in history and traditions',
          location: 'Collalto Sabino, Italy',
          destinations: ['collalto_sabino'],
          images: [
            '/destinations/collalto_sabino/collalto_sabino_1_medium.jpg',
            '/destinations/collalto_sabino/collalto_sabino_2_medium.jpg',
            '/destinations/collalto_sabino/collalto_sabino_3_medium.jpg'
          ]
        },
        {
          day: 1,
          title: 'Santo Stefano di Sessanio',
          description: 'Visit the suggestive stone village of Santo Stefano di Sessanio, a UNESCO heritage site',
          location: 'Santo Stefano di Sessanio, Italy',
          destinations: ['santo_stefano_di_sessanio'],
          images: [
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_1_medium.jpg',
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_2_medium.jpg',
            '/destinations/santo_stefano_di_sessanio/santo_stefano_di_sessanio_3_medium.jpg'
          ]
        },
        {
          day: 1,
          title: 'Overnight at Sextantio Hotel',
          description: 'Relax in the elegant Sextantio Hotel, where tradition and comfort meet',
          location: 'Santo Stefano di Sessanio, Italy',
          activities: ['sextantio']
        },
        {
          day: 2,
          title: 'Rocca Calascio',
          description: 'Explore the ancient fortress of Rocca Calascio, one of the highest castles in Italy',
          location: 'Rocca Calascio, Italy',
          destinations: ['rocca_calascio'],
          images: [
            '/destinations/rocca_calascio/rocca_calascio_1_medium.jpg',
            '/destinations/rocca_calascio/rocca_calascio_2_medium.jpg',
            '/destinations/rocca_calascio/rocca_calascio_3_medium.jpg'
          ]
        },
        {
          day: 2,
          title: 'Arrosticini at Mucciante',
          description: 'Taste authentic Abruzzese arrosticini at the historic Mucciante',
          location: 'Mucciante, Italy',
          activities: ['arrosticini_mucciante'],
          images: [
            '/activities/arrosticini_mucciante/arrosticini_mucciante_1.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_2.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_3.jpg',
            '/activities/arrosticini_mucciante/arrosticini_mucciante_4.jpg'
          ]
        },
        {
          day: 2,
          title: 'Campo Imperatore',
          description: 'Admire the enchanting plateau of Campo Imperatore, the "Little Tibet" of Abruzzo',
          location: 'Campo Imperatore, Italy',
          destinations: ['campo_imperatore'],
          images: [
            '/destinations/gran_sasso/gran_sasso_1_medium.jpg',
            '/destinations/gran_sasso/gran_sasso_2_medium.jpg',
            '/destinations/gran_sasso/gran_sasso_3_medium.jpg'
          ]
        },
        {
          day: 2,
          title: 'Overnight at XXX',
          description: 'Rest in comfortable accommodation immersed in Abruzzese nature',
          location: 'Campo Imperatore, Italy',
          info: ['pernottamento a XXX'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 3,
          title: 'Deer spotting',
          description: 'Experience the thrill of observing deer in their natural habitat',
          location: 'Abruzzo National Park, Italy',
          activities: ['deer_spotting'],
          images: [
            '/activities/deer_spotting/deer_spotting_1.jpg',
            '/activities/deer_spotting/deer_spotting_2.jpg',
            '/activities/deer_spotting/deer_spotting_3.jpg',
            '/activities/deer_spotting/deer_spotting_4.jpg',
            '/activities/deer_spotting/deer_spotting_5.jpg',
            '/activities/deer_spotting/deer_spotting_6.jpg',
            '/activities/deer_spotting/deer_spotting_7.jpg'
          ]
        },
        {
          day: 3,
          title: 'Scanno',
          description: 'Discover the characteristic village of Scanno and its traditions',
          location: 'Scanno, Italy',
          destinations: ['scanno'],
          images: [
            '/destinations/scanno/scanno_1.jpg',
            '/destinations/scanno/scanno_2.jpg',
            '/destinations/scanno/scanno_3.jpg',
            '/destinations/scanno/scanno_4.jpg',
            '/destinations/scanno/scanno_5.jpg',
            '/destinations/scanno/scanno_6.jpg',
            '/destinations/scanno/scanno_7.jpg',
            '/destinations/scanno/scanno_8.jpg',
            '/destinations/scanno/scanno_9.jpg',
            '/destinations/scanno/scanno_10.jpg',
            '/destinations/scanno/scanno_11.jpg',
            '/destinations/scanno/scanno_12.jpg',
            '/destinations/scanno/scanno_13.jpg',
            '/destinations/scanno/scanno_14.jpg',
            '/destinations/scanno/scanno_15.jpg',
            '/destinations/scanno/scanno_16.jpg',
            '/destinations/scanno/scanno_17.jpg',
            '/destinations/scanno/scanno_18.jpg',
            '/destinations/scanno/scanno_19.jpg'
          ]
        },
        {
          day: 3,
          title: 'Wine tasting at Azienda Tilli',
          description: 'Enjoy activities at Azienda Tilli, local wine tasting and typical products',
          location: 'Azienda Tilli, Italy',
          activities: ['cantina_tilli'],
          images: [
            '/activities/cantina_tilli/cantina_tilli_1_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_2_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_3_medium.webp',
            '/activities/cantina_tilli/cantina_tilli_4_medium.webp'
          ]
        },
        {
          day: 3,
          title: 'Overnight at camping',
          description: 'Sleep under the stars in a cozy camping immersed in nature',
          location: 'Camping, Italy',
          info: ['pernottamento al camping'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 4,
          title: 'Trabocchi Coast',
          description: 'Start your adventure along the suggestive Trabocchi Coast',
          location: 'Trabocchi Coast, Italy',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 4,
          title: 'Overnight at camping',
          description: 'Continue sleeping under the stars along the coast',
          location: 'Trabocchi Coast, Italy',
          info: ['pernottamento al camping'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 5,
          title: 'Trabocchi Coast',
          description: 'Continue exploring the magical Trabocchi Coast',
          location: 'Trabocchi Coast, Italy',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 5,
          title: 'Overnight at camping',
          description: 'Continue sleeping under the stars along the coast',
          location: 'Trabocchi Coast, Italy',
          info: ['pernottamento al camping'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 6,
          title: 'Trabocchi Coast',
          description: 'Last day exploring the Trabocchi Coast',
          location: 'Trabocchi Coast, Italy',
          destinations: ['trabocchi_coast'],
          images: [
            '/destinations/trabocchi_coast/trabocchi_coast_1.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_2.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_3.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_4.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_5.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_6.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_7.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_8.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_9.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_10.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_11.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_12.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_13.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_14.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_15.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_16.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_17.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_18.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_19.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_20.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_21.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_22.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_23.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_24.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_25.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_26.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_27.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_28.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_29.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_30.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_31.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_32.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_33.jpg',
            '/destinations/trabocchi_coast/trabocchi_coast_34.jpg'
          ]
        },
        {
          day: 6,
          title: 'Overnight at camping',
          description: 'Last night under the stars along the coast',
          location: 'Trabocchi Coast, Italy',
          info: ['pernottamento al camping'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 7,
          title: 'Continue to enjoy the last moments of your vacation',
          description: 'Enjoy the last moments of this fantastic vacation in Abruzzo',
          location: 'Trabocchi Coast, Italy',
          info: ['continua a gustarti gli ultimi momenti della vacanza'],
          images: [
            '/trips/summer-trip.webp'
          ]
        },
        {
          day: 7,
          title: 'Return to airport',
          description: 'Goodbye! Our team will accompany you to the airport for your return',
          location: 'Airport',
          info: ['ritorno all\'aeroporto'],
          images: [
            '/trips/summer-trip.webp'
          ]
        }
      ],
      faqs: [
        {
          question: 'What is included in the price?',
          answer: 'The price includes guided tours, meals, and all activities mentioned in the itinerary.'
        },
        {
          question: 'What is the difficulty level?',
          answer: 'This trip is suitable for all fitness levels with moderate walking involved.'
        }
      ],
      mapLocation: {
        name: 'Rocca Calascio',
        address: '67020 Rocca Calascio, Province of L\'Aquila, Italy',
        coordinates: { lat: 42.3283, lng: 13.6889 }
      }
    }
  };

  return tripsData[tripId] || null;
}; 

// Function to get minimum price per person for a trip
export const getMinimumPricePerPerson = async (tripId: string): Promise<string | null> => {
  try {
    // Map tripId to database name directly
    const databaseName = tripId === 'summer-trip' ? 'Summer Trip' : 
                        tripId === 'winter-trip' ? 'Winter Trip' : 
                        tripId;
    
    const response = await tripsApi.getTripsByName(databaseName);
    if (!response.success || !response.trip) return null;
    
    const trip = response.trip;
    
    // Get all prices per person (they are already per person in the database)
    const prices = [
      trip.priceSingle,
      trip.priceDuo,
      trip.priceTrio,
      trip.pricePocker
    ].filter(price => price && price > 0);
    
    if (prices.length === 0) return null;
    
    // Find minimum price
    const minPrice = Math.min(...prices);
    
    // Format as currency
    return `€${minPrice}`;
  } catch (error) {
    console.error('Error getting minimum price:', error);
    return null;
  }
};

// Hook to get minimum price per person for a trip
export const useMinimumPrice = (tripId: string): { price: string | null; loading: boolean } => {
  const [price, setPrice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrice = async () => {
      setLoading(true);
      try {
        const minPrice = await getMinimumPricePerPerson(tripId);
        setPrice(minPrice);
      } catch (error) {
        console.error('Error fetching minimum price:', error);
        setPrice(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (tripId) {
      fetchPrice();
    }
  }, [tripId]);
  
  return { price, loading };
};
